import * as React from 'react';

import {Button} from '../button/button';
import {RemoveButton} from '../button/remove-button/remove-button';
import {Card} from '../card/card';
import type {IconProps} from '../icon/svg-icon-wrapper';
import {MessageGroup} from '../message-group/message-group';

import styles from './cta-banner.module.css';

type Props = {
    /** This should be a component that takes a `size` prop */
    icon?: React.ComponentType<IconProps>;
    /** Optional title to include on first line */
    title?: string;
    /** Body text of the CTA. Must be included. */
    text: string;
    /** This will be shown as blue text, and will be styled as hovered when any of the CTA itself is hovered. */
    buttonText: string;
    /** If the CTA is to visit another page, use an href and clicking the banner will visit the link */
    href?: string;
    /** Optional setting to open href in a new tab. */
    newTab?: boolean;
    /** Optional max width of text. Set to `null` for no constraint. */
    maxWidth: number | null | undefined;
    /** Optional callback to execute when button/banner is clicked */
    onPrimaryAction?: () => void;
    /** Including this callback adds a dismiss button, and onDismiss is called after animating out. */
    onDismiss?: () => Promise<any> | undefined;
    /** This will allow the CTA banner to change the height of the card */
    fitHeight?: boolean;
    /** This is used to change the border radius of the card */
    borderRadius?: number;
};

type State = {
    isClosing: boolean;
};

/**
 * Use this component when you want the user to perform an action, or
 * visit a support page to learn more about upgrading, or something similar.
 */
export class CTABanner extends React.PureComponent<Props, State> {
    static defaultProps = {
        maxWidth: 650,
    };

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isClosing: false,
        };
    }

    render() {
        const Icon = this.props.icon;

        const bodyText = (
            <React.Fragment>
                {this.props.text}{' '}
                <span className={styles['primary-action']}>
                    {this.props.onPrimaryAction ? (
                        <Button variant='text' size='big' onClick={this.handlePrimaryButtonClick}>
                            {this.props.buttonText}
                        </Button>
                    ) : (
                        this.props.buttonText
                    )}
                </span>
            </React.Fragment>
        );

        return (
            <Card
                hasBorder={true}
                fitHeight={this.props.fitHeight}
                borderRadius={this.props.borderRadius}
            >
                <div
                    onTransitionEnd={this.handleTransitionEnd}
                    className={this.state.isClosing ? styles.closing : styles['cta-banner']}
                >
                    <a
                        href={this.props.href}
                        target={this.props.newTab ? '_blank' : '_self'}
                        rel='noopener noreferrer'
                        onClick={this.props.onPrimaryAction}
                        className={styles['hover-trap']}
                    >
                        <MessageGroup
                            icon={Icon}
                            title={this.props.title}
                            text={bodyText}
                            maxWidth={this.props.maxWidth}
                        />
                    </a>
                    {this.props.onDismiss ? (
                        <div className={styles['remove-button']}>
                            <RemoveButton onClick={this.handleRemoveClick} />
                        </div>
                    ) : null}
                </div>
            </Card>
        );
    }

    handlePrimaryButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (this.props.onPrimaryAction) {
            this.props.onPrimaryAction();
        }
    };

    handleRemoveClick = () => {
        this.setState({isClosing: true});
    };

    handleTransitionEnd = () => {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };
}
